import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { KeyboardLayoutId, MiniCheckpoint } from '../types';
import { playMissChime } from '../lib/audio/missChime';
import { playSuccessChime, primeSuccessChime } from '../lib/audio/successChime';
import { accuracy as accuracyFn } from '../lib/stats';
import { shuffleWords } from '../lib/wordSprint';
import Keyboard from './Keyboard';

export interface MiniCheckpointFinishPayload {
  wordsCompleted: number;
  wordsSkipped: number;
  startedAt: string;
  durationMs: number;
  accuracy: number;
  passed: boolean;
}

interface MiniCheckpointScreenProps {
  checkpoint: MiniCheckpoint;
  keyboardLayout: KeyboardLayoutId;
  audioEnabled?: boolean;
  onFinish: (payload: MiniCheckpointFinishPayload) => void;
  onCancel: () => void;
}

export default function MiniCheckpointScreen({
  checkpoint,
  keyboardLayout,
  audioEnabled = true,
  onFinish,
  onCancel,
}: MiniCheckpointScreenProps) {
  const words = useMemo(
    () => shuffleWords(checkpoint.sprintWords),
    [checkpoint.id, checkpoint.sprintWords],
  );
  const durationMs = checkpoint.durationMs;

  const [phase, setPhase] = useState<'ready' | 'playing'>('ready');
  const [wordIdx, setWordIdx] = useState(0);
  const [cursor, setCursor] = useState(0);
  const [wordsCompleted, setWordsCompleted] = useState(0);
  const [wordsSkipped, setWordsSkipped] = useState(0);
  const [correctKeystrokes, setCorrectKeystrokes] = useState(0);
  const [totalKeystrokes, setTotalKeystrokes] = useState(0);
  const [now, setNow] = useState(Date.now());
  const [lastPress, setLastPress] = useState<
    { key: string; correct: boolean } | undefined
  >();
  const [awaitingSpace, setAwaitingSpace] = useState(false);
  const [clockStarted, setClockStarted] = useState(false);

  const clockStartedRef = useRef(false);
  const startedAtRef = useRef('');
  const endsAtRef = useRef(0);
  const armedAtRef = useRef(0);
  const finishedRef = useRef(false);
  const completedRef = useRef(0);
  const skippedRef = useRef(0);
  const correctKeystrokesRef = useRef(0);
  const totalKeystrokesRef = useRef(0);

  const startClock = useCallback(() => {
    if (clockStartedRef.current) return;
    clockStartedRef.current = true;
    const t = Date.now();
    startedAtRef.current = new Date(t).toISOString();
    endsAtRef.current = t + durationMs;
    setNow(t);
    setClockStarted(true);
  }, [durationMs]);

  const currentWord = words[wordIdx] ?? '';

  const finish = useCallback(() => {
    if (finishedRef.current) return;
    finishedRef.current = true;
    if (!startedAtRef.current) {
      startedAtRef.current = new Date(armedAtRef.current).toISOString();
    }
    const acc = accuracyFn(
      correctKeystrokesRef.current,
      totalKeystrokesRef.current,
    );
    const passed =
      completedRef.current >= checkpoint.minWords &&
      acc >= checkpoint.minAccuracy;
    onFinish({
      wordsCompleted: completedRef.current,
      wordsSkipped: skippedRef.current,
      startedAt: startedAtRef.current,
      durationMs,
      accuracy: acc,
      passed,
    });
  }, [checkpoint.minAccuracy, checkpoint.minWords, durationMs, onFinish]);

  const advanceWord = useCallback(() => {
    setAwaitingSpace(false);
    setWordIdx((i) => (i + 1) % words.length);
    setCursor(0);
    setLastPress(undefined);
  }, [words.length]);

  const skipWord = useCallback(() => {
    if (audioEnabled) playMissChime();
    skippedRef.current += 1;
    setWordsSkipped(skippedRef.current);
    advanceWord();
  }, [advanceWord, audioEnabled]);

  const completeWord = useCallback(() => {
    completedRef.current += 1;
    setWordsCompleted(completedRef.current);
    advanceWord();
  }, [advanceWord]);

  const startPlaying = useCallback(() => {
    if (audioEnabled) primeSuccessChime();
    armedAtRef.current = Date.now();
    finishedRef.current = false;
    completedRef.current = 0;
    skippedRef.current = 0;
    correctKeystrokesRef.current = 0;
    totalKeystrokesRef.current = 0;
    setWordsCompleted(0);
    setWordsSkipped(0);
    setCorrectKeystrokes(0);
    setTotalKeystrokes(0);
    setWordIdx(0);
    setCursor(0);
    setAwaitingSpace(false);
    clockStartedRef.current = false;
    setClockStarted(false);
    setPhase('playing');
  }, [audioEnabled]);

  useEffect(() => {
    if (phase !== 'playing') return;
    const id = window.setInterval(() => setNow(Date.now()), 100);
    return () => window.clearInterval(id);
  }, [phase]);

  useEffect(() => {
    if (phase !== 'playing' || !clockStarted) return;
    if (now >= endsAtRef.current) finish();
  }, [phase, clockStarted, now, finish]);

  useEffect(() => {
    if (phase !== 'playing') return;

    const handler = (e: KeyboardEvent) => {
      if (finishedRef.current) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        finish();
        return;
      }

      if (e.key === 'Backspace') {
        e.preventDefault();
        return;
      }

      if (e.key === ' ') {
        e.preventDefault();
        startClock();
        if (awaitingSpace) {
          setLastPress({ key: ' ', correct: true });
          completeWord();
        }
        return;
      }

      if (e.key.length !== 1) return;
      e.preventDefault();
      startClock();

      const word = words[wordIdx];
      if (!word || awaitingSpace) return;

      const expected = word[cursor];
      if (expected === undefined) return;

      totalKeystrokesRef.current += 1;
      setTotalKeystrokes(totalKeystrokesRef.current);
      const same = e.key.toLowerCase() === expected.toLowerCase();
      if (same) {
        correctKeystrokesRef.current += 1;
        setCorrectKeystrokes(correctKeystrokesRef.current);
        setLastPress({ key: e.key, correct: true });
        const next = cursor + 1;
        if (next >= word.length) {
          if (audioEnabled) playSuccessChime();
          setAwaitingSpace(true);
        } else setCursor(next);
      } else {
        setLastPress({ key: e.key, correct: false });
        skipWord();
      }
    };

    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [
    phase,
    words,
    wordIdx,
    cursor,
    awaitingSpace,
    audioEnabled,
    finish,
    skipWord,
    completeWord,
    startClock,
  ]);

  const remainingMs =
    phase === 'playing'
      ? clockStarted
        ? Math.max(0, endsAtRef.current - now)
        : durationMs
      : durationMs;

  const expectedChar = awaitingSpace ? ' ' : currentWord[cursor];
  const liveAcc =
    totalKeystrokes === 0 ? 1 : correctKeystrokes / totalKeystrokes;
  const secs = Math.round(durationMs / 1000);

  if (words.length === 0) {
    return (
      <Shell>
        <p className="text-faint">Ingen ord defineret for denne kontrol.</p>
        <button type="button" onClick={onCancel} className="btn-secondary mt-4">
          Tilbage
        </button>
      </Shell>
    );
  }

  return (
    <Shell>
      <header className="flex items-center justify-between gap-4">
        <div>
          <p className="label text-indigo-700 dark:text-indigo-300">
            🚪 Mini-checkpoint · {secs} sek
          </p>
          <h1 className="mt-1 text-2xl font-semibold text-strong">
            {checkpoint.title}
          </h1>
          <p className="text-sm text-muted">{checkpoint.description}</p>
        </div>
        <button type="button" onClick={onCancel} className="btn-secondary shrink-0">
          Afslut
        </button>
      </header>

      {phase === 'ready' ? (
        <div className="surface mx-auto w-full max-w-xl border border-indigo-200/80 p-6 text-center animate-fade-in-up dark:border-indigo-500/20">
          <p className="text-sm text-strong">{checkpoint.goal}</p>
          <p className="mt-2 text-xs text-muted">
            Bestå med mindst {checkpoint.minWords} ord og{' '}
            {(checkpoint.minAccuracy * 100).toFixed(0)}% nøjagtighed.
          </p>
          <button
            type="button"
            autoFocus
            onClick={startPlaying}
            className="mt-6 rounded-xl bg-indigo-500 px-6 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-indigo-400"
          >
            Start kontrol
          </button>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-4 gap-3">
            <Stat label="Tid" value={formatCountdown(remainingMs)} />
            <Stat label="Ord" value={String(wordsCompleted)} />
            <Stat label="Nøjagtighed" value={`${(liveAcc * 100).toFixed(0)}%`} />
            <Stat label="Sprunget over" value={String(wordsSkipped)} />
          </div>
          <RoundPanel>
            <p className="label mb-3">Skriv dette ord</p>
            <p className="font-mono text-4xl font-semibold tracking-wide text-strong">
              {currentWord ? (
                [...currentWord].map((ch, i) => {
                  const done = awaitingSpace || i < cursor;
                  const active = !awaitingSpace && i === cursor;
                  let cls = 'text-zinc-400 dark:text-zinc-500';
                  if (done) cls = 'text-emerald-600 dark:text-emerald-300';
                  else if (active)
                    cls =
                      'text-zinc-900 dark:text-zinc-100 bg-indigo-100 dark:bg-indigo-500/10 rounded-sm border-b-2 border-indigo-500';
                  return (
                    <span key={i} className={cls}>
                      {ch}
                    </span>
                  );
                })
              ) : (
                <span className="text-muted">—</span>
              )}
            </p>
            {awaitingSpace && (
              <p className="mt-4 text-sm font-medium text-emerald-600 dark:text-emerald-300">
                Tryk mellemrum for næste ord
              </p>
            )}
          </RoundPanel>
          <Keyboard
            layoutId={keyboardLayout}
            nextExpectedKey={expectedChar}
            lastPressedKey={lastPress?.key}
            lastPressCorrect={lastPress?.correct}
            unlockedKeys={checkpoint.unlockedKeys}
            hintLevel="none"
            showFingerDot={false}
          />
        </>
      )}
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-6">{children}</div>
  );
}

function RoundPanel({ children }: { children: React.ReactNode }) {
  return (
    <div className="surface p-8 text-center ring-2 ring-indigo-200/60 dark:ring-indigo-500/25">
      {children}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="surface-soft px-4 py-3">
      <p className="label">{label}</p>
      <p className="mt-1 font-mono text-2xl text-strong">{value}</p>
    </div>
  );
}

function formatCountdown(ms: number): string {
  const total = Math.ceil(ms / 1000);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}
